import type {
  ArchivedMember,
  ArchivedProfile,
  ArchiveMemberInput,
  AssignChannelsInput,
  AuditPage,
  BienBan,
  CaiDatCongTy,
  DanhDauThanhToanInput,
  DatKenhNhomInput,
  DeleteEligibility,
  DeleteMemberResult,
  DieuChinh,
  DoiNguKenh,
  DoiSoatKy,
  DongTyLeTuNgayInput,
  ListChannelsResponse,
  GoogleAccountRecord,
  GoogleAccountRecordInput,
  HoSoThue,
  InviteMemberInput,
  InviteResult,
  KhoanChi,
  LoaiThueTncn,
  LuuCaiDatCongTyInput,
  LuuChinhSachKenhInput,
  LuuDieuChinhInput,
  LuuHoSoThueInput,
  LuuKhoanChiInput,
  LuuNgayVanHanhInput,
  LuuNhomInput,
  LuuQuyDoiInput,
  LuuTyLeInput,
  MoLaiDoiSoatInput,
  Nhom,
  RestoreMemberResult,
  RevokeInviteResult,
  RoleWithPermissions,
  TeamMember,
  TeamSummary,
  ThuNhapCuaToi,
  UpdateMemberInput,
  UpdateRolePermissionsInput,
} from '@qlk/shared';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';
import { goiDuocApi, khoaKy, thamSoKy, type KyTrang } from '../../lib/kyTrang.js';

const GOC = '/api/v1/team';

export const KHOA = {
  thanhVien: ['team', 'members'] as const,
  luuTru: ['team', 'archived'] as const,
  tomTat: ['team', 'summary'] as const,
  vaiTro: ['team', 'roles'] as const,
  taiKhoan: ['team', 'google-accounts'] as const,
};

/** Bản ghi sổ bảo mật kèm số kênh còn kết nối (module sync cung cấp). */
export interface TaiKhoanGoogleDong extends GoogleAccountRecord {
  connectedChannels: number;
}

/* -------------------------------- Đọc -------------------------------- */

export function useThanhVien() {
  return useQuery<TeamMember[], LoiApi>({
    queryKey: KHOA.thanhVien,
    queryFn: () => api.get<TeamMember[]>(`${GOC}/users`),
  });
}

export function useTomTatTeam() {
  return useQuery<TeamSummary, LoiApi>({
    queryKey: KHOA.tomTat,
    queryFn: () => api.get<TeamSummary>(`${GOC}/summary`),
  });
}

export function useVaiTro() {
  return useQuery<RoleWithPermissions[], LoiApi>({
    queryKey: KHOA.vaiTro,
    queryFn: () => api.get<RoleWithPermissions[]>(`${GOC}/roles`),
  });
}

/**
 * Danh sách kênh để chọn khi giao việc. Dùng endpoint đã chốt của module 01;
 * người không có quyền `channels` thì truy vấn hỏng và giao diện tự lùi về danh
 * sách kênh suy ra từ dữ liệu thành viên.
 */
export function useDanhSachKenhGon(batDau = true) {
  return useQuery<ListChannelsResponse, LoiApi>({
    queryKey: ['team', 'channels'],
    queryFn: () => api.get<ListChannelsResponse>('/api/v1/channels?limit=100'),
    enabled: batDau,
    retry: false,
  });
}

/** Tab "Lưu trữ": nhân sự đã nghỉ, mới nghỉ trước. */
export function useThanhVienLuuTru(batDau = true) {
  return useQuery<ArchivedMember[], LoiApi>({
    queryKey: KHOA.luuTru,
    queryFn: () => api.get<ArchivedMember[]>(`${GOC}/users/luu-tru`),
    enabled: batDau,
  });
}

/** Hồ sơ chỉ xem của một người đã lưu trữ. */
export function useHoSoLuuTru(id: string) {
  return useQuery<ArchivedProfile, LoiApi>({
    queryKey: ['team', 'archived', id],
    queryFn: () => api.get<ArchivedProfile>(`${GOC}/users/${id}/ho-so-luu-tru`),
  });
}

export interface LocNhatKy {
  module?: string;
  tu?: string;
  den?: string;
}

/**
 * Nhật ký thao tác của một người: lọc theo module và khoảng ngày, tải thêm theo
 * cursor. Mỗi trang 25 dòng — nhật ký của người làm lâu năm rất dài.
 */
export function useNhatKyThanhVien(id: string, loc: LocNhatKy) {
  return useInfiniteQuery<
    AuditPage,
    LoiApi,
    InfiniteData<AuditPage>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: ['team', 'audit', id, loc],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ limit: '25' });
      if (pageParam) q.set('cursor', pageParam);
      if (loc.module) q.set('module', loc.module);
      if (loc.tu) q.set('tu', loc.tu);
      if (loc.den) q.set('den', loc.den);
      return api.get<AuditPage>(`${GOC}/users/${id}/nhat-ky?${q.toString()}`);
    },
    getNextPageParam: (trang) => trang.nextCursor ?? undefined,
  });
}

/**
 * Xoá cứng được hay không, kèm số liệu để giải thích.
 *
 * Hỏi máy chủ chứ không suy từ dữ liệu đang có trên màn hình: điều kiện trải
 * khắp 13 bảng của 7 module.
 */
export function useKhaNangXoa(id: string, batDau: boolean) {
  return useQuery<DeleteEligibility, LoiApi>({
    queryKey: ['team', 'can-delete', id],
    queryFn: () => api.get<DeleteEligibility>(`${GOC}/users/${id}/kha-nang-xoa`),
    enabled: batDau,
    retry: false,
  });
}

export function useTaiKhoanGoogle() {
  return useQuery<TaiKhoanGoogleDong[], LoiApi>({
    queryKey: KHOA.taiKhoan,
    queryFn: () => api.get<TaiKhoanGoogleDong[]>(`${GOC}/tai-khoan-google`),
  });
}

/* ------------------------------ Mutation ----------------------------- */

function lamMoiThanhVien(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: KHOA.thanhVien }),
    qc.invalidateQueries({ queryKey: KHOA.luuTru }),
    qc.invalidateQueries({ queryKey: KHOA.tomTat }),
  ]);
}

export function useMoiThanhVien() {
  const qc = useQueryClient();
  return useMutation<InviteResult, LoiApi, InviteMemberInput>({
    mutationFn: (input) => api.post<InviteResult>(`${GOC}/moi`, input),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

/**
 * Hai nút của người đang ở trạng thái "đã mời".
 *
 * `guiMail = false` → "Tạo lại link" (chỉ lấy link, không gửi thư).
 * `guiMail = true`  → "Gửi lại email mời".
 *
 * Cả hai phát link **mới** và cho link cũ hết hạn: token trong database chỉ là
 * bản băm nên không đọc lại được link cũ.
 */
export function useLamMoiLoiMoi() {
  const qc = useQueryClient();
  return useMutation<InviteResult, LoiApi, { id: string; guiMail: boolean }>({
    mutationFn: ({ id, guiMail }) =>
      goiApi<InviteResult>(`${GOC}/users/${id}/${guiMail ? 'gui-lai-moi' : 'link-moi'}`, {
        method: 'POST',
      }),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

/**
 * Quản trị đặt lại mật khẩu cho người khác: máy chủ gửi link đặt mật khẩu qua
 * email. Phản hồi **không** chứa mật khẩu, nhưng **luôn** có `inviteUrl` để
 * quản trị sao chép khi email chưa cấu hình hoặc gửi lỗi.
 */
export function useDatLaiMatKhau() {
  const qc = useQueryClient();
  return useMutation<InviteResult, LoiApi, { id: string }>({
    mutationFn: ({ id }) =>
      goiApi<InviteResult>(`${GOC}/users/${id}/dat-lai-mat-khau`, { method: 'POST' }),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

export function useCapNhatThanhVien() {
  const qc = useQueryClient();
  return useMutation<TeamMember, LoiApi, { id: string } & UpdateMemberInput>({
    mutationFn: ({ id, ...than }) =>
      goiApi<TeamMember>(`${GOC}/users/${id}`, { method: 'PATCH', body: JSON.stringify(than) }),
    onSuccess: async () => {
      await lamMoiThanhVien(qc);
      await qc.invalidateQueries({ queryKey: KHOA.vaiTro });
    },
  });
}

/**
 * Khoá / mở khoá tài khoản một thành viên đã kích hoạt.
 *
 * Khoá **không xoá** gì: lịch sử và nhật ký thao tác giữ nguyên, phân công kênh
 * giữ nguyên. Máy chủ thu hồi mọi phiên của người đó nên người đang online bị
 * đẩy ra ở request kế tiếp.
 *
 * Ràng buộc "không tự khoá mình" và "không khoá quản trị viên hoạt động cuối
 * cùng" do máy chủ kiểm; giao diện ẩn nút chỉ là lớp thứ hai nên lỗi 400 vẫn
 * phải hiện ra cho người bấm.
 */
export function useDoiKhoaThanhVien() {
  const qc = useQueryClient();
  return useMutation<TeamMember, LoiApi, { id: string; khoa: boolean }>({
    mutationFn: ({ id, khoa }) =>
      goiApi<TeamMember>(`${GOC}/users/${id}/${khoa ? 'khoa' : 'mo-khoa'}`, { method: 'POST' }),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

/**
 * Thu hồi lời mời chưa nhận: link chết ngay, tài khoản chưa kích hoạt và mọi
 * dòng lời mời của email đó bị xoá hẳn. Chỉ dùng cho người ở trạng thái
 * "đã mời" — người đã dùng tài khoản thì khoá, không xoá.
 */
export function useThuHoiLoiMoi() {
  const qc = useQueryClient();
  return useMutation<RevokeInviteResult, LoiApi, { id: string }>({
    mutationFn: ({ id }) => api.del<RevokeInviteResult>(`${GOC}/users/${id}/loi-moi`),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

/**
 * Lưu trữ một thành viên đã kích hoạt (nhân sự nghỉ việc).
 *
 * Khác "Khoá tài khoản": lưu trữ là vĩnh viễn, gỡ phân công kênh để người khác
 * nhận, và chụp "hồ sơ khi nghỉ" để tra lại về sau. Lịch sử thao tác giữ nguyên.
 */
export function useLuuTruThanhVien() {
  const qc = useQueryClient();
  return useMutation<TeamMember, LoiApi, { id: string } & ArchiveMemberInput>({
    mutationFn: ({ id, ...than }) =>
      goiApi<TeamMember>(`${GOC}/users/${id}/luu-tru`, {
        method: 'POST',
        body: JSON.stringify(than),
      }),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

/**
 * Khôi phục người đã lưu trữ. Máy chủ phát link đặt lại mật khẩu và gửi email;
 * **không** gán lại kênh cũ — quản trị gán tay.
 */
export function useKhoiPhucThanhVien() {
  const qc = useQueryClient();
  return useMutation<RestoreMemberResult, LoiApi, { id: string }>({
    mutationFn: ({ id }) => api.post<RestoreMemberResult>(`${GOC}/users/${id}/khoi-phuc`),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

/** Xoá cứng tài khoản thêm nhầm. Máy chủ kiểm lại điều kiện, không tin giao diện. */
export function useXoaThanhVien() {
  const qc = useQueryClient();
  return useMutation<DeleteMemberResult, LoiApi, { id: string }>({
    mutationFn: ({ id }) => api.del<DeleteMemberResult>(`${GOC}/users/${id}`),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

export function useGiaoKenh() {
  const qc = useQueryClient();
  return useMutation<TeamMember, LoiApi, { id: string } & AssignChannelsInput>({
    mutationFn: ({ id, ...than }) =>
      goiApi<TeamMember>(`${GOC}/users/${id}/kenh`, {
        method: 'PUT',
        body: JSON.stringify(than),
      }),
    onSuccess: () => lamMoiThanhVien(qc),
  });
}

export function useLuuQuyen() {
  const qc = useQueryClient();
  return useMutation<RoleWithPermissions, LoiApi, { id: string } & UpdateRolePermissionsInput>({
    mutationFn: ({ id, ...than }) =>
      goiApi<RoleWithPermissions>(`${GOC}/roles/${id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify(than),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.vaiTro });
    },
  });
}

export function useLuuTaiKhoanGoogle() {
  const qc = useQueryClient();
  return useMutation<GoogleAccountRecord, LoiApi, { id?: string; input: GoogleAccountRecordInput }>(
    {
      mutationFn: ({ id, input }) =>
        id
          ? goiApi<GoogleAccountRecord>(`${GOC}/tai-khoan-google/${id}`, {
              method: 'PATCH',
              body: JSON.stringify(input),
            })
          : api.post<GoogleAccountRecord>(`${GOC}/tai-khoan-google`, input),
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: KHOA.taiKhoan });
        await qc.invalidateQueries({ queryKey: KHOA.tomTat });
      },
    },
  );
}

/* ==================================================================== */
/* Nhóm kênh, đội ngũ chia sẻ và chi phí vận hành                       */
/* ==================================================================== */

export const KHOA_CHIA_SE = {
  nhom: (period: string) => ['team', 'nhom', period] as const,
  doiNgu: (channelId: string, ky: string) => ['team', 'doi-ngu', channelId, ky] as const,
  chiPhi: (channelId: string, period?: string) =>
    ['team', 'chi-phi', channelId, period ?? 'tat-ca'] as const,
};

export function useNhom(period: string, batDau = true) {
  return useQuery<Nhom[], LoiApi>({
    queryKey: KHOA_CHIA_SE.nhom(period),
    queryFn: () => api.get<Nhom[]>(`${GOC}/nhom?period=${period}`),
    enabled: batDau,
  });
}

/**
 * Đội ngũ, chi phí và chuỗi chia của một kênh trong **đúng kỳ của trang**.
 *
 * Kỳ tháng gửi `?period=YYYY-MM`; mọi kỳ khác (28/90/365 ngày, một năm, toàn
 * bộ thời gian, khoảng tự chọn) gửi `?from=&to=` — máy chủ tính cùng một chuỗi
 * chia trên khoảng đó, chỉ bỏ phần lỗ cộng dồn (xem `chiaSe.TuyChonChia`).
 */
export function useDoiNguKenh(channelId: string, ky: KyTrang) {
  return useQuery<DoiNguKenh, LoiApi>({
    queryKey: KHOA_CHIA_SE.doiNgu(channelId, khoaKy(ky)),
    queryFn: () => api.get<DoiNguKenh>(`${GOC}/kenh/${channelId}/doi-ngu?${thamSoKy(ky)}`),
    enabled: goiDuocApi(ky),
  });
}

export function useChiPhiKenh(channelId: string, period: string, batDau = true) {
  return useQuery<KhoanChi[], LoiApi>({
    queryKey: KHOA_CHIA_SE.chiPhi(channelId, period),
    queryFn: () => api.get<KhoanChi[]>(`${GOC}/kenh/${channelId}/chi-phi?period=${period}`),
    enabled: batDau,
  });
}

/**
 * Mọi thay đổi tỷ lệ/chi phí/nhóm đều đổi con số của bảng Quản lý kênh (enricher
 * `chiaSe`), nên làm mới luôn `['channels']` thay vì chỉ khoá của module này.
 */
function lamMoiChiaSe(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['team', 'nhom'] }),
    qc.invalidateQueries({ queryKey: ['team', 'doi-ngu'] }),
    qc.invalidateQueries({ queryKey: ['team', 'chi-phi'] }),
    qc.invalidateQueries({ queryKey: ['channels'] }),
  ]);
}

export function useLuuNhom() {
  const qc = useQueryClient();
  return useMutation<Nhom, LoiApi, { id?: string } & LuuNhomInput>({
    mutationFn: ({ id, ...than }) =>
      id
        ? goiApi<Nhom>(`${GOC}/nhom/${id}`, { method: 'PATCH', body: JSON.stringify(than) })
        : api.post<Nhom>(`${GOC}/nhom`, than),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useLuuTruNhom() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, LoiApi, { id: string; luuTru: boolean }>({
    mutationFn: ({ id, luuTru }) =>
      api.post<{ ok: boolean }>(`${GOC}/nhom/${id}/luu-tru`, { luuTru }),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useDatKenhNhom() {
  const qc = useQueryClient();
  return useMutation<{ them: number; bo: number }, LoiApi, { id: string } & DatKenhNhomInput>({
    mutationFn: ({ id, ...than }) =>
      goiApi<{ them: number; bo: number }>(`${GOC}/nhom/${id}/kenh`, {
        method: 'PUT',
        body: JSON.stringify(than),
      }),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

/** Kết quả lưu một dòng tỷ lệ — giao diện cần biết có tự gán quyền kênh không. */
export interface KetQuaLuuTyLe {
  id: string;
  dongCuDaDong: string | null;
  daGanQuyenKenh: boolean;
  canhBao: string | null;
}

export function useLuuTyLeTruongNhom() {
  const qc = useQueryClient();
  return useMutation<KetQuaLuuTyLe, LoiApi, { teamId: string } & LuuTyLeInput>({
    mutationFn: ({ teamId, ...than }) =>
      api.post<KetQuaLuuTyLe>(`${GOC}/nhom/${teamId}/truong-nhom`, than),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useLuuTyLeKenh() {
  const qc = useQueryClient();
  return useMutation<KetQuaLuuTyLe, LoiApi, { channelId: string } & LuuTyLeInput>({
    mutationFn: ({ channelId, ...than }) =>
      api.post<KetQuaLuuTyLe>(`${GOC}/kenh/${channelId}/ty-le`, than),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useDongTyLe() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, LoiApi, { id: string } & DongTyLeTuNgayInput>({
    mutationFn: ({ id, ...than }) => api.post<{ id: string }>(`${GOC}/ty-le/${id}/dong`, than),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useLuuNgayVanHanh() {
  const qc = useQueryClient();
  return useMutation<
    { channelId: string; ngayBatDauVanHanh: string | null },
    LoiApi,
    { channelId: string } & LuuNgayVanHanhInput
  >({
    mutationFn: ({ channelId, ...than }) =>
      goiApi(`${GOC}/kenh/${channelId}/ngay-van-hanh`, {
        method: 'PUT',
        body: JSON.stringify(than),
      }),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useLuuChinhSachKenh() {
  const qc = useQueryClient();
  return useMutation<{ channelId: string }, LoiApi, { channelId: string } & LuuChinhSachKenhInput>({
    mutationFn: ({ channelId, ...than }) =>
      goiApi(`${GOC}/kenh/${channelId}/chinh-sach`, {
        method: 'PUT',
        body: JSON.stringify(than),
      }),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useLuuKhoanChi() {
  const qc = useQueryClient();
  return useMutation<KhoanChi, LoiApi, { channelId: string } & LuuKhoanChiInput>({
    mutationFn: ({ channelId, ...than }) =>
      api.post<KhoanChi>(`${GOC}/kenh/${channelId}/chi-phi`, than),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

export function useXoaKhoanChi() {
  const qc = useQueryClient();
  return useMutation<{ channelId: string }, LoiApi, { id: string }>({
    mutationFn: ({ id }) => api.del<{ channelId: string }>(`${GOC}/chi-phi/${id}`),
    onSuccess: () => lamMoiChiaSe(qc),
  });
}

/* ---------------------------- Đối soát nhân sự ------------------------ */

/**
 * Mọi thao tác đối soát đổi số của trang này **và** của tổng quan (khối nhắc
 * "còn N bản nháp chưa chốt"), nên làm mới cả hai.
 */
function lamMoiDoiSoat(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['team', 'doi-soat'] }),
    qc.invalidateQueries({ queryKey: ['dashboard'] }),
  ]);
}

/** Kỳ xem: tháng, hoặc quý (chỉ xem). */
export type KyDoiSoat = { period: string; quy?: undefined } | { quy: string; period?: undefined };

function truyVanKy(ky: KyDoiSoat): string {
  return ky.quy ? `quy=${ky.quy}` : `period=${ky.period ?? ''}`;
}

export function useDoiSoat(ky: KyDoiSoat, batDau = true) {
  return useQuery<DoiSoatKy, LoiApi>({
    queryKey: ['team', 'doi-soat', ky.quy ?? ky.period] as const,
    queryFn: () => api.get<DoiSoatKy>(`${GOC}/doi-soat?${truyVanKy(ky)}`),
    enabled: batDau,
  });
}

export function duongDanXuatExcelDoiSoat(period: string): string {
  return `${GOC}/doi-soat/xuat-excel?period=${period}`;
}

export function useLuuDieuChinh() {
  const qc = useQueryClient();
  return useMutation<DieuChinh, LoiApi, LuuDieuChinhInput>({
    mutationFn: (than) => api.post<DieuChinh>(`${GOC}/doi-soat/dieu-chinh`, than),
    onSuccess: () => lamMoiDoiSoat(qc),
  });
}

export function useXoaDieuChinh() {
  const qc = useQueryClient();
  return useMutation<DieuChinh, LoiApi, { id: string }>({
    mutationFn: ({ id }) => api.del<DieuChinh>(`${GOC}/doi-soat/dieu-chinh/${id}`),
    onSuccess: () => lamMoiDoiSoat(qc),
  });
}

export interface KetQuaChotDoiSoat {
  period: string;
  userId: string;
  phienBan: number;
  tongPhanChia: number;
  tongDieuChinh: number;
  phaiTra: number;
  soDong: number;
}

export function useChotDoiSoat() {
  const qc = useQueryClient();
  return useMutation<KetQuaChotDoiSoat, LoiApi, { period: string; userId: string }>({
    mutationFn: ({ period, userId }) =>
      api.post<KetQuaChotDoiSoat>(`${GOC}/doi-soat/${period}/nguoi/${userId}/chot`),
    onSuccess: () => lamMoiDoiSoat(qc),
  });
}

/** Người bị bỏ qua khi chốt cả kỳ, kèm kênh còn thiếu ngày vận hành. */
export interface NguoiBoQuaKhiChot {
  userId: string;
  tenNguoi: string;
  kenhThieuNgayVanHanh: { channelId: string; tenKenh: string }[];
}

export interface KetQuaChotTatCa {
  period: string;
  daChot: KetQuaChotDoiSoat[];
  boQua: NguoiBoQuaKhiChot[];
}

export function useChotTatCa() {
  const qc = useQueryClient();
  return useMutation<KetQuaChotTatCa, LoiApi, { period: string }>({
    mutationFn: ({ period }) => api.post<KetQuaChotTatCa>(`${GOC}/doi-soat/${period}/chot-tat-ca`),
    onSuccess: () => lamMoiDoiSoat(qc),
  });
}

export function useMoLaiDoiSoat() {
  const qc = useQueryClient();
  return useMutation<
    { period: string; userId: string; phienBan: number },
    LoiApi,
    { period: string; userId: string } & MoLaiDoiSoatInput
  >({
    mutationFn: ({ period, userId, ...than }) =>
      api.post(`${GOC}/doi-soat/${period}/nguoi/${userId}/mo-lai`, than),
    onSuccess: () => lamMoiDoiSoat(qc),
  });
}

export function useDanhDauThanhToan() {
  const qc = useQueryClient();
  return useMutation<
    { period: string; userId: string; ngay: string; phaiTra: number },
    LoiApi,
    { period: string; userId: string } & DanhDauThanhToanInput
  >({
    mutationFn: ({ period, userId, ...than }) =>
      api.post(`${GOC}/doi-soat/${period}/nguoi/${userId}/thanh-toan`, than),
    onSuccess: () => lamMoiDoiSoat(qc),
  });
}

/* ----------------- Cài đặt công ty, hồ sơ thuế, biên bản -------------- */

export function useCaiDatCongTy() {
  return useQuery<CaiDatCongTy, LoiApi>({
    queryKey: ['team', 'cai-dat-cong-ty'] as const,
    queryFn: () => api.get<CaiDatCongTy>(`${GOC}/cai-dat-cong-ty`),
  });
}

export function useLuuCaiDatCongTy() {
  const qc = useQueryClient();
  return useMutation<CaiDatCongTy, LoiApi, LuuCaiDatCongTyInput>({
    mutationFn: (than) => api.put<CaiDatCongTy>(`${GOC}/cai-dat-cong-ty`, than),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['team', 'cai-dat-cong-ty'] }),
        qc.invalidateQueries({ queryKey: ['team', 'doi-soat'] }),
      ]),
  });
}

export function useListHoSoThue() {
  return useQuery<HoSoThue[], LoiApi>({
    queryKey: ['team', 'ho-so-thue'] as const,
    queryFn: () => api.get<HoSoThue[]>(`${GOC}/ho-so-thue`),
  });
}

export function useLuuHoSoThue() {
  const qc = useQueryClient();
  return useMutation<HoSoThue, LoiApi, { userId: string } & LuuHoSoThueInput>({
    mutationFn: ({ userId, ...than }) => api.put<HoSoThue>(`${GOC}/ho-so-thue/${userId}`, than),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['team', 'ho-so-thue'] }),
        qc.invalidateQueries({ queryKey: ['team', 'thu-nhap'] }),
      ]),
  });
}

export function useBienBan(payoutId: string) {
  return useQuery<BienBan, LoiApi>({
    queryKey: ['team', 'bien-ban', payoutId] as const,
    queryFn: () => api.get<BienBan>(`${GOC}/doi-soat/bien-ban/${payoutId}`),
  });
}

export interface KetQuaQuyDoi {
  period: string;
  userId: string;
  phienBan: number;
  tyGiaVnd: number;
  phaiTraVnd: number;
  thuNhapChiuThueVnd: number;
  thueTncn: number;
  thueTncnPct: number;
  loaiThue: LoaiThueTncn;
  thueGhiDe: boolean;
  thucChuyenVnd: number;
  thueDaDu: boolean;
}

export function useLuuQuyDoi() {
  const qc = useQueryClient();
  return useMutation<KetQuaQuyDoi, LoiApi, { period: string; userId: string } & LuuQuyDoiInput>({
    mutationFn: ({ period, userId, ...than }) =>
      api.put(`${GOC}/doi-soat/${period}/nguoi/${userId}/quy-doi`, than),
    onSuccess: () => lamMoiDoiSoat(qc),
  });
}

export function useThuNhapCuaToi(period?: string) {
  return useQuery<ThuNhapCuaToi, LoiApi>({
    queryKey: ['team', 'thu-nhap', period ?? 'gan-day'] as const,
    queryFn: () =>
      api.get<ThuNhapCuaToi>(
        period ? `${GOC}/thu-nhap-cua-toi?period=${period}` : `${GOC}/thu-nhap-cua-toi`,
      ),
  });
}

export function duongDanBaoCaoThue(phamVi: string, moc: string): string {
  return `${GOC}/bao-cao-thue/xuat-excel?phamVi=${phamVi}&moc=${moc}`;
}
